import { useState } from "react";

import { MediaFrame, MediaSheet, MediaSheetSkeleton, RatioSection } from "@/components/content-programs/media-tile";
import { MediaLightbox } from "@/components/content-programs/submission-dialog";
import { useRatioGroups } from "@/hooks/useRatioGroups";
import type { ProgramSample } from "@/services/contentProgram.service";

/** The brand's reference creative, on the same sheet as everything else; a tile opens at full size. */
export default function SampleContentGallery({ samples }: { samples: ProgramSample[] }) {
  const [open, setOpen] = useState<ProgramSample | null>(null);
  const { orderedGroups, loading, getRatioLabel, getMetaRatioLabel } = useRatioGroups(samples);

  if (!samples.length) return null;

  return (
    <>
      {loading ? (
        <MediaSheetSkeleton count={samples.length} />
      ) : (
        <div className="space-y-4">
          {orderedGroups.map((group) => (
            <RatioSection key={group.key} ratio={group.key} count={group.items.length}>
              <MediaSheet ratio={group.key}>
                {group.items.map((sample) => (
                  <button
                    key={sample.id}
                    type="button"
                    onClick={() => setOpen(sample)}
                    aria-label="Open sample"
                    className="group block w-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                  >
                    <MediaFrame
                      ratio={getRatioLabel(sample.id)}
                      poster={sample.thumbnailUrl || (sample.mediaType === "IMAGE" ? sample.mediaUrl : null)}
                      isVideo={sample.mediaType === "VIDEO"}
                      className="group-hover:ring-foreground/30"
                    >
                      {sample.mediaType === "VIDEO" && !sample.thumbnailUrl && (
                        <video
                          src={sample.mediaUrl}
                          muted
                          playsInline
                          preload="metadata"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      )}
                    </MediaFrame>
                  </button>
                ))}
              </MediaSheet>
            </RatioSection>
          ))}
        </div>
      )}

      <MediaLightbox
        open={open !== null}
        onOpenChange={(next) => !next && setOpen(null)}
        mediaUrl={open?.mediaUrl}
        mediaType={open?.mediaType}
        ratioLabel={open ? getMetaRatioLabel(open.id) : null}
        title="Sample"
      />
    </>
  );
}
